import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Observable, Subject, of } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  map,
  switchMap,
  takeUntil
} from 'rxjs/operators';
import {
  ResidentGlobalSearchResult,
  ResidentGlobalSearchResultDetail
} from '../../security-deposit/common/models/global-search.model';
import { ResidentsService } from '../../security-deposit/residents/services/residents.service';
import { Router } from '@angular/router';
import { InviteQueryParameter, InviteStatusCode } from '../../security-deposit/invites/models/invites.model';
import {
  ResidentGroup,
  ResidentsQueryParameter,
  ResidentStatusCode
} from '../../security-deposit/residents/models/residents.model';
import {
  ApplicationQueryParameter,
  ApplicationsGroupStatusCode
} from '../../security-deposit/applications/models/applications.model';
import { ClaimsQueryParameter } from '../../security-deposit/claims/models/claims.model';

interface SearchGroup {
  label: string;
  items: ResidentGlobalSearchResultDetail[];
}

@Component({
  standalone: false,
  selector: 'app-shell-global-search',
  templateUrl: './shell-global-search.component.html',
  styleUrls: ['./shell-global-search.component.scss']
})
export class ShellGlobalSearchComponent implements OnInit, OnDestroy {
  private readonly defaultPageSize = 50;

  searchControl = new FormControl('');
  resultGroups: SearchGroup[] = [];
  showNoResults = false;

  private destroy$ = new Subject<void>();

  constructor(
    private residentsService: ResidentsService,
    private router: Router
  ) { }

  ngOnInit(): void {
    this.searchControl.valueChanges.pipe(
      map(value => (value || '').toString().trim()),
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(value => this.fetchResults(value)),
      takeUntil(this.destroy$)
    ).subscribe(result => {
      if (!result) {
        return;
      }

      this.setGroups(result);
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getDisplayName(item: ResidentGlobalSearchResultDetail): string {
    return item.fullName || item.email || '';
  }

  onItemSelected(
    item: ResidentGlobalSearchResultDetail,
    groupLabel: string,
    event: any
  ): void {
    if (!event?.isUserInput) {
      return;
    }

    const search = this.getSearchValue(item);

    switch (groupLabel) {
      case 'Invited':
        this.router.navigate(['security-deposit/invites'], {
          queryParams: this.withSearch({
            sortBy: InviteQueryParameter.inviteDate,
            sortDesc: true,
            statusCodes: [InviteStatusCode.sent],
            pageNo: 1,
            pageSize: this.defaultPageSize,
            search
          })
        });
        break;
      case 'Application':
        this.router.navigate(['security-deposit/applications'], {
          queryParams: this.withSearch({
            sortBy: ApplicationQueryParameter.lastInteractionDate,
            sortDesc: true,
            progressStatusCode: ApplicationsGroupStatusCode.inProgress,
            pageNo: 1,
            pageSize: this.defaultPageSize,
            search
          })
        });
        break;
      case 'Active':
        this.navigateToResidents({
          residentGroup: ResidentGroup.activeLease,
          statusCodes: [
            ResidentStatusCode.approved,
            ResidentStatusCode.funded,
            ResidentStatusCode.pendingRefund,
            ResidentStatusCode.chargebacked
          ],
          isHasClaim: false,
          search
        });
        break;
      case 'Lease Ended':
        this.navigateToResidents({
          residentGroup: ResidentGroup.postLease,
          statusCodes: [
            ResidentStatusCode.leaseEnded,
            ResidentStatusCode.evicted
          ],
          isHasClaim: false,
          search
        });
        break;
      case 'Completed':
        this.navigateToResidents({
          residentGroup: ResidentGroup.legacy,
          statusCodes: [
            ResidentStatusCode.archived,
            ResidentStatusCode.refunded,
            ResidentStatusCode.claimPaidOut
          ],
          isHasClaim: false,
          search
        });
        break;
      case 'Claims':
        this.router.navigate(['security-deposit/claims'], {
          queryParams: this.withSearch({
            sortBy: ClaimsQueryParameter.updateTimeUtc,
            sortDesc: true,
            pageNo: 1,
            pageSize: this.defaultPageSize,
            search
          })
        });
        break;
      default:
        break;
    }
  }

  private fetchResults(value: string): Observable<ResidentGlobalSearchResult | null> {
    if (value.length < 3) {
      this.clearResults();
      return of(null);
    }

    this.showNoResults = false;

    return this.residentsService.getGlobalSearchResults(value).pipe(
      catchError(() => of(new ResidentGlobalSearchResult()))
    );
  }

  private setGroups(result: ResidentGlobalSearchResult): void {
    this.resultGroups = [
      { label: 'Invited', items: result.invited ?? [] },
      { label: 'Application', items: result.application ?? [] },
      { label: 'Active', items: result.active ?? [] },
      { label: 'Lease Ended', items: result.leaseEnded ?? [] },
      { label: 'Completed', items: result.completed ?? [] },
      { label: 'Claims', items: result.claims ?? [] }
    ].filter(group => group.items.length > 0);

    this.showNoResults = this.resultGroups.length === 0;
  }

  private clearResults(): void {
    this.resultGroups = [];
    this.showNoResults = false;
  }

  private getSearchValue(item: ResidentGlobalSearchResultDetail): string | undefined {
    return item.email?.trim() || this.getDisplayName(item);
  }

  private navigateToResidents(options: {
    residentGroup: ResidentGroup;
    statusCodes?: ResidentStatusCode[];
    isHasClaim: boolean;
    search?: string;
  }): void {
    this.router.navigate(['security-deposit/residents'], {
      queryParams: this.withSearch({
        sortBy: ResidentsQueryParameter.leaseEndDate,
        sortDesc: false,
        residentGroup: options.residentGroup,
        isHasClaim: options.isHasClaim,
        statusCodes: options.statusCodes,
        pageNo: 1,
        pageSize: this.defaultPageSize
      }, options.search)
    });
  }

  private withSearch(params: Record<string, any>, search?: string): Record<string, any> {
    if (search) {
      return { ...params, search };
    }

    return params;
  }
}
